<script setup lang="ts">
import { Icon } from '~ds/components/ui/icon'
import { ICONS } from '~ds/lib/icons'
import {
  VDrawerContent,
  VDrawerOverlay,
  VDrawerPortal,
} from '~kui/vendor/vaul/drawer'
import { DialogContent, DialogOverlay, DialogPortal } from 'reka-ui'
import { useI18n } from 'vue-i18n'

import { useCredenzaContext } from './credenzaContext'
import UniCredenzaClose from './UniCredenzaClose.vue'
import UniCredenzaDescription from './UniCredenzaDescription.vue'
import UniCredenzaTitle from './UniCredenzaTitle.vue'

/**
 * A dialog is announced by its name and its description, and the underlying primitive points
 * `aria-labelledby`/`aria-describedby` at whatever a `UniCredenzaTitle`/`UniCredenzaDescription`
 * renders. Content that shows neither — a credenza holding a whole page of its own — names itself
 * through these instead, and they are rendered for screen readers only.
 *
 * Content that renders its own `UniCredenzaTitle` or `UniCredenzaDescription` leaves these unset:
 * two of either would share the one id the primitive points at. A heading or a paragraph that is
 * merely visible is not one of those — it names nothing, so content showing a title it did not
 * wrap in `UniCredenzaTitle` still has to pass that same string here.
 */
interface Props {
  title?: string;
  description?: string;
  // Desktop dialog width only — the mobile drawer is always edge-to-edge. `sm` is the
  // compact decision-modal width from the design; `md` is the default dialog; `lg` is the
  // wide surface a side-by-side comparison needs.
  size?: 'md' | 'sm' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  description: undefined,
  size: 'md',
})

/**
 * Where the dialog opens focused, handed to the caller as the trap's own hook rather than left to
 * a race. Reka's focus scope emits this the moment it mounts and autofocuses its first tabbable
 * element only if nothing prevented it, so `@open-auto-focus.prevent` is the one ordering against
 * the trap that is contractual — a caller focusing from `onMounted` instead is racing it.
 *
 * Forwarded explicitly because this component's root is a fragment: attribute fallthrough drops
 * listeners with nowhere single to land.
 *
 * Desktop only. Vaul's drawer content prevents reka's mount autofocus itself and re-emits nothing,
 * so the mobile branch never autofocuses and has no event to forward — a caller that needs initial
 * focus on both breakpoints still needs a mounted-time fallback for it.
 *
 * `closeAutoFocus` is the matching hook on the way out, on both breakpoints. A credenza opened by
 * its `open` prop rather than a trigger has nowhere to send focus back to on its own, so a caller
 * that opens one that way prevents it and focuses whatever opened it.
 */
const emit = defineEmits<{
  openAutoFocus: [event: Event];
  closeAutoFocus: [event: Event];
}>()

// This content mounts inside a teleported portal, where the local i18n scope can't be resolved
// (intlify then warns "Not found parent scope"). The credenza only uses global keys, so bind the
// global scope explicitly.
const { t } = useI18n({ useScope: 'global' })
const { isMobile, dismissable, dismissOnEscape, fullScreen, dragProgress, showDragHandle, showCloseButton, animated } = useCredenzaContext()

/**
 * Both breakpoints animate from a stylesheet this component does not own — vaul drives the drawer
 * off its own transition, reka off the keyframes below — so stillness is one class that outranks
 * either, applied to the two elements that move.
 */
const stillClass = computed(() => (animated.value ? '' : 'credenza-still'))

/**
 * The same fact as the class above, said in the one place a restyle cannot move it: what motion the
 * credenza is under is behaviour, and this is what a test reads it off.
 */
const animatedAttr = computed(() => (animated.value ? 'true' : 'false'))

const blockIfNotDismissable = (e: Event) => {
  if (!dismissable.value) {
    e.preventDefault()
  }
}

const blockEscapeIfNotDismissable = (e: Event) => {
  if (!dismissOnEscape.value) {
    e.preventDefault()
  }
}

const overlayStyle = computed(() => {
  if (dragProgress.value > 0) {
    return { opacity: 1 - dragProgress.value }
  }
  return {}
})

const mobileClasses = computed(() =>
  twMerge(
    cva('fixed z-60 flex flex-col rounded-t-3xl bg-background outline-none', {
      variants: {
        fullScreen: {
          true: 'mt-1 inset-0 h-[100vh] shadow-2xl',
          false: 'inset-x-0 bottom-0 max-h-[92vh] shadow-2xl',
        },
      },
    })({ fullScreen: fullScreen.value }),
  ),
)

const desktopClasses = computed(() =>
  cva('credenza-content fixed z-60 flex flex-col bg-white outline-none', {
    variants: {
      fullScreen: {
        true: 'credenza-content--full-screen inset-0 rounded-0 w-full',
        false: 'left-1/2 top-1/2 max-w-[94vw] max-h-[90vh] md:max-h-[85vh] overflow-y-auto scrollbar-hidden rounded-2xl -translate-x-1/2 -translate-y-1/2 border border-border shadow-xl',
      },
      size: {
        md: '',
        sm: '',
        lg: '',
      },
    },
    compoundVariants: [
      { fullScreen: false, size: 'md', class: 'w-[520px] lg:w-[700px]' },
      { fullScreen: false, size: 'sm', class: 'w-[465px]' },
      { fullScreen: false, size: 'lg', class: 'w-[520px] lg:w-3xl' },
    ],
  })({ fullScreen: fullScreen.value, size: props.size }),
)
</script>

<template>
  <!-- Mobile: vaul drawer handles its own animations -->
  <template v-if="isMobile">
    <VDrawerPortal>
      <VDrawerOverlay
        class="fixed inset-0 z-60 bg-primary/30"
        :class="stillClass"
        :data-credenza-animated="animatedAttr"
        :style="overlayStyle"
        @pointerdown="blockIfNotDismissable"
      />
      <VDrawerContent
        :class="[mobileClasses, stillClass]"
        :data-credenza-animated="animatedAttr"
        @escape-key-down="blockIfNotDismissable"
        @close-auto-focus="(event: Event) => emit('closeAutoFocus', event)"
      >
        <UniCredenzaTitle v-if="title" class="sr-only">
          {{ title }}
        </UniCredenzaTitle>
        <UniCredenzaDescription v-if="description" class="sr-only">
          {{ description }}
        </UniCredenzaDescription>
        <div v-if="showDragHandle" class="mx-auto shrink-0 px-4 mt-2">
          <div class="mx-auto h-1 w-28 rounded-full bg-primary/40" />
        </div>
        <template v-if="showCloseButton">
          <UniCredenzaClose
            :as-child="false"
            class="absolute top-2 right-2 z-50 hover:cursor-pointer bg-muted/50 hover:bg-accent p-2 rounded-full"
            :aria-label="t('unified.common.close')"
          >
            <Icon :name="ICONS.cancel" size="lg" />
          </UniCredenzaClose>
        </template>
        <slot />
      </VDrawerContent>
    </VDrawerPortal>
  </template>

  <!-- Desktop: reka-ui dialog matches KUICoreDialog -->
  <template v-else>
    <DialogPortal>
      <DialogOverlay
        class="credenza-overlay fixed inset-0 z-60 bg-primary/30 backdrop-blur"
        :class="stillClass"
        :data-credenza-animated="animatedAttr"
      />
      <DialogContent
        :class="[desktopClasses, stillClass]"
        :data-credenza-animated="animatedAttr"
        @pointer-down-outside="blockIfNotDismissable"
        @interact-outside="blockIfNotDismissable"
        @escape-key-down="blockEscapeIfNotDismissable"
        @open-auto-focus="(event: Event) => emit('openAutoFocus', event)"
        @close-auto-focus="(event: Event) => emit('closeAutoFocus', event)"
      >
        <UniCredenzaTitle v-if="title" class="sr-only">
          {{ title }}
        </UniCredenzaTitle>
        <UniCredenzaDescription v-if="description" class="sr-only">
          {{ description }}
        </UniCredenzaDescription>
        <template v-if="showCloseButton">
          <UniCredenzaClose
            :as-child="false"
            class="absolute top-2 right-2 z-50 hover:cursor-pointer bg-muted/50 hover:bg-accent p-2 rounded-full"
            :aria-label="t('unified.common.close')"
          >
            <Icon :name="ICONS.cancel" size="lg" />
          </UniCredenzaClose>
        </template>
        <slot />
      </DialogContent>
    </DialogPortal>
  </template>
</template>

<style scoped>
.credenza-still {
  animation: none !important;
  transition: none !important;
}

.credenza-overlay[data-state="open"] {
  animation: credenza-fade-in 0.5s cubic-bezier(0.32, 0.72, 0, 1);
}
.credenza-overlay[data-state="closed"] {
  animation: credenza-fade-out 0.5s cubic-bezier(0.32, 0.72, 0, 1) forwards;
}

/* Non-fullScreen dialog: fade only (scale would break translate centering) */
.credenza-content[data-state="open"] {
  animation: credenza-fade-in 0.5s cubic-bezier(0.32, 0.72, 0, 1);
}
.credenza-content[data-state="closed"] {
  animation: credenza-fade-out 0.5s cubic-bezier(0.32, 0.72, 0, 1) forwards;
}

/* Full-screen dialog: slide from bottom (matches vaul drawer feel) */
.credenza-content--full-screen[data-state="open"] {
  animation: credenza-slide-in 0.5s cubic-bezier(0.32, 0.72, 0, 1);
}
.credenza-content--full-screen[data-state="closed"] {
  animation: credenza-slide-out 0.5s cubic-bezier(0.32, 0.72, 0, 1) forwards;
}

@keyframes credenza-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes credenza-fade-out {
  from { opacity: 1; }
  to { opacity: 0; }
}

@keyframes credenza-slide-in {
  from { transform: translate3d(0, 100%, 0); }
  to { transform: translate3d(0, 0, 0); }
}
@keyframes credenza-slide-out {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(0, 100%, 0); }
}
</style>
